import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Target, Zap, FileText, Users, Shield, Heart } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const About = () => {
  const navigate = useNavigate();

  useSEO({
    title: "About Resume Flow AI - Speed-First ATS Resume Builder",
    description: "Learn how Resume Flow AI helps job seekers build ATS-optimized resumes fast. Paste content, structure instantly, and apply to dozens of jobs in minutes.",
    canonical: "/about",
  });

  return (
    <div className="min-h-screen gradient-hero relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="bg-orb bg-orb-primary w-[400px] h-[400px] -top-48 -left-48 opacity-25" />
      <div className="bg-orb bg-orb-accent w-[350px] h-[350px] top-1/3 -right-32 opacity-20" />
      
      <Header />
      
      <main className="container mx-auto px-4 pt-24 pb-16 relative z-10">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-6 animate-slide-up">
            About Resume Flow AI
          </h1>
          <p className="text-lg text-muted-foreground animate-slide-up-delay-1">
            A personal project that started as a way to streamline my own job applications, 
            now available for everyone to use.
          </p>
        </div>
        
        {/* Story Section */}
        <div className="max-w-4xl mx-auto">
          <div className="glass-effect-strong rounded-3xl p-8 sm:p-10 mb-12">
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
              The Story Behind This Project
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Resume Flow AI began as a personal project, a tool I built to simplify and speed up 
                my own resume creation process. I wanted something that could quickly analyze and
                optimize resumes for Applicant Tracking Systems without the hassle.
              </p>
              <p>
                Using AI and a deep understanding of how ATS systems parse resumes, I created a tool 
                that analyzes, optimizes, and tailors resumes to maximize their chances of reaching 
                real recruiters.
              </p>
              <p>
                What started as a college project turned into something I realized could help others. 
                Now Resume Flow AI is available for everyone to use and benefit from.
              </p>
            </div>
          </div>
          
          {/* Features Grid */}
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-8 text-center">
            What Makes Us Different
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <div className="glass-effect rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">ATS-First Approach</h3>
              <p className="text-sm text-muted-foreground">
                Every feature is designed with ATS compatibility in mind. No fancy graphics, no tables, 
                just clean, parseable content that gets results.
              </p>
            </div>
            
            <div className="glass-effect rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">AI-Powered Tailoring</h3>
              <p className="text-sm text-muted-foreground">
                Paste a job description, upload your resume, and get a version tailored specifically 
                for that role with relevant keywords highlighted.
              </p>
            </div>
            
            <div className="glass-effect rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">Professional Exports</h3>
              <p className="text-sm text-muted-foreground">
                Download your resume in PDF or DOCX format, both optimized for ATS systems while 
                looking professional for human readers.
              </p>
            </div>
            
            <div className="glass-effect rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">Privacy Focused</h3>
              <p className="text-sm text-muted-foreground">
                Your resume data is yours. We don't sell or share your information with third parties.
              </p>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="text-center glass-effect-strong rounded-3xl p-10">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
              Ready to Improve Your Resume?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Start using Resume Flow AI today and increase your chances of landing interviews.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate("/auth")}
              className="gradient-button text-primary-foreground"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          {/* Feedback Section */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Facing any issues or have suggestions? Your feedback helps us improve.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate("/contact")}
              className="gap-2"
            >
              <Heart className="w-4 h-4" />
              Share Feedback
            </Button>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/30 py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2024 Resume Flow AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;